import { useCallback, ReactNode, RefObject, useState } from 'react';
import {
  useGetSearchCategory,
  useUpdateSearchCategory,
} from '@legacy/apis/searchAPI/categoriesAPI';
import { ISearchCategoryItem } from '@legacy/apis/types/searchCategoriesAPI';
import { Heading } from '@legacy/components/ui-components/Heading';
import { Icon } from '@legacy/components/ui-components/Icon';
import { Stack } from '@legacy/components/ui-components/Stack';
import { useTranslation } from 'react-i18next';
import { translationAnyText } from '@legacy/utils/translation';
import { getKeyboardFocusableElements } from '@legacy/utils/accessibility';
import styles from './SearchCategoryPanel.module.scss';
import {
  onKeyDown,
  useDefaultCategoryItemFocus,
  useResetActiveIndex,
} from './SearchCategoryPanelHookHelper';
import { useSearchStore } from '@/stores/search';

const icons: Record<ISearchCategoryItem['name'], ReactNode> = {
  Customer: <Icon iconName="customer" size="small" />,
  Invoice: <Icon iconName="finance" size="small" />,
  Employee: <Icon iconName="employee" size="small" />,
  Project: <Icon iconName="project" size="small" />,
  Contact: <Icon iconName="contact" size="small" />,
};

const updateCategoryList = (
  categoryList: ISearchCategoryItem[],
  targetName: string,
  targetSelectedStatus: boolean,
): ISearchCategoryItem[] =>
  categoryList?.map(({ name, isSelected }) =>
    targetName === name
      ? { name, isSelected: targetSelectedStatus }
      : { name, isSelected },
  );

export const updateCategoryListForTest = updateCategoryList;

const SearchCategorySearchBox = ({ name, isSelected }: ISearchCategoryItem) => {
  const { t } = useTranslation('header');
  const { mutate: searchCategoryAPIUpdate } = useUpdateSearchCategory();
  const { categoryList, updateStore } = useSearchStore();

  const onSelectChange = useCallback(() => {
    const latestList = updateCategoryList(categoryList, name, !isSelected);
    updateStore({
      type: 'UPDATE_CATEGORY_LIST',
      payload: latestList,
    });
    searchCategoryAPIUpdate(latestList);
  }, [name, categoryList, searchCategoryAPIUpdate, isSelected]);

  return (
    <button
      type="button"
      id={`SearchCategory${name}`}
      data-automation-id={`SearchCategory${name}`}
      className={styles.SearchFilterCategoryItem}
      onClick={onSelectChange}
      aria-pressed={isSelected}
    >
      <span
        className={styles.Checkmark}
        data-automation-id={`SearchCategoryCheckbox${name}`}
      >
        <Icon iconName="check" />
      </span>
      <span className={styles.SearchFilterCategoryItemInner}>
        {icons[name]}
        <span>{translationAnyText(t, `SearchCategory${name}sLabel`)}</span>
      </span>
    </button>
  );
};

interface SearchCategoryPanelProps {
  searchFilterCategoriesRef: RefObject<HTMLDivElement | null>;
  focusInput?: () => void;
}

const reloadKeyboardSelectedElement = (selectedElement: Element) => {
  if (selectedElement) {
    (selectedElement as HTMLElement)?.focus();
  }
};

// eslint-disable-next-line react/display-name
export default ({
  searchFilterCategoriesRef,
  focusInput,
}: SearchCategoryPanelProps) => {
  const { t } = useTranslation('header');
  const { isCategoryOpen: isSearchCategoryPanelOpen, categoryList } =
    useSearchStore();
  const { isSearchCategoryListEmpty } = useGetSearchCategory();
  const [activeIndex, setActiveIndex] = useState(0);

  useResetActiveIndex(isSearchCategoryPanelOpen, setActiveIndex);
  useDefaultCategoryItemFocus(
    isSearchCategoryPanelOpen,
    searchFilterCategoriesRef,
  );

  const handleKeyDown = (event: any) => {
    const focusableElementsCount = getKeyboardFocusableElements(
      searchFilterCategoriesRef?.current,
    ).length;
    onKeyDown({
      focusableElementsCount,
      activeIndex,
      setActiveIndex,
      event,
      focusInput,
    });
  };

  if (isSearchCategoryPanelOpen && searchFilterCategoriesRef)
    reloadKeyboardSelectedElement(
      getKeyboardFocusableElements(searchFilterCategoriesRef?.current)?.[
        activeIndex
      ],
    );

  return !isSearchCategoryListEmpty && isSearchCategoryPanelOpen ? (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions
    <div
      data-automation-id="MainSearchCategoryPanel"
      id="MainSearchCategoryFilter"
      className={styles.SearchCategories}
      ref={searchFilterCategoriesRef}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <Stack verticalMargin="medium">
        <Heading level={2} as={3} medium>
          {t('SearchCategoryFilterHeading')}
        </Heading>
        <div>
          <ul className={styles.SearchCategoriesList}>
            {categoryList?.map(({ name, isSelected }) => (
              <li key={`search-category-${name}`}>
                <SearchCategorySearchBox name={name} isSelected={isSelected} />
              </li>
            ))}
          </ul>
        </div>
      </Stack>
    </div>
  ) : null;
};
